import { useEffect, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR, { mutate as swrMutate } from 'swr';
import { isAxiosError } from 'axios';
import { Download, Eye, EyeOff, HardDrive, Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useT } from '@/contexts/I18nContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  deleteNVR,
  fetchNvrCameras,
  listZoneNVRs,
  updateNVR,
  type FetchCamerasResponse,
  type NVRRecord,
  type NVRUpsert,
} from '@/api/nvrs';
import { extractApiError } from '@/utils/apiError';
import { AdminTunnelButton } from './AdminTunnelButton';

interface Props {
  zoneId: number | string;
}

/**
 * Brick-9: equipment-tab NVR section. The NVR sibling of {@link RouterPanel}.
 *
 * Renders one card per NVR row returned by `GET /equipment_discovery/zones/:zoneId/nvrs/`.
 * NVR is plural-per-zone on the BE (Hikvision NVRs are typically one
 * per rack — multiple racks → multiple NVRs), so this renders a stack
 * rather than the OneToOne shape RouterPanel assumes.
 *
 * Empty-state contract (mirrors RouterPanel per 2026-05-17 spec §7):
 * if the list is empty (or 404) we render `null` and let the canonical
 * `<AddDeviceDropdown>` own the add affordance. No empty-state card.
 *
 * Identity columns (`model`, `serial_number`, `firmware_version`,
 * `device_id`, `mac_address`) come from the discovery primitive in
 * autofill mode and are exposed read-only in this panel — editing them
 * by hand would diverge from the device reality. Operators edit the
 * dialable fields (`ip_address`, `port`, `username`, `password`,
 * `notes`); the rest is "what discovery told us".
 *
 * SWR key `zone-nvrs:${zoneId}` is mutated by:
 *   - EquipmentSettings.handleDeviceCreated (after add)
 *   - this panel itself after save/delete (`reload()`)
 *
 * `<DiscoverButton>` integration lives in a follow-up — this panel
 * currently exposes the NVR data flow only; the per-card discover
 * affordance lands when the master flow is wired in.
 */
export default function NVRPanel({ zoneId }: Props) {
  const { t } = useT();
  const swrKey = `zone-nvrs:${zoneId}`;
  const { data, error, isLoading } = useSWR<
    NVRRecord[],
    { response?: { status?: number } }
  >(
    swrKey,
    () => listZoneNVRs(zoneId).then(r => r.data.results),
    { shouldRetryOnError: false },
  );

  const notFound = error?.response?.status === 404;
  const otherError = error && !notFound;
  const list = data ?? [];
  const exists = list.length > 0;

  const reload = () => swrMutate(swrKey);

  // No NVRs and we know it: render nothing. Add path is in
  // AddDeviceDropdown — same contract as RouterPanel.
  if (!isLoading && !exists && (notFound || !error)) return null;
  // Initial flash: hide until we know.
  if (isLoading && !data && !error) return null;
  // Error case is rare here (listZoneNVRs returns {results:[]} on empty),
  // but keep the surface in case the endpoint regresses.
  if (otherError && !exists) {
    return (
      <Card>
        <CardContent>
          <ServerStatusNotice variant="card" error={error} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {list.map(nvr => (
        <NVRCard key={nvr.id} nvr={nvr} onChanged={reload} />
      ))}
    </>
  );
}

function NVRCard({ nvr, onChanged }: { nvr: NVRRecord; onChanged: () => void }) {
  const { t } = useT();
  const [editing, setEditing] = useState(false);

  return (
    <Card>
      <CardContent className="space-y-4">
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
          <HardDrive className="size-4" />
          {t('equipment.nvr.title')}
          {/* NVR model is shown in the body field grid below; keep
              the header to just "NVR" so cards line up visually. */}
        </h2>

        {editing ? (
          <NVRForm
            nvr={nvr}
            onSaved={() => {
              setEditing(false);
              onChanged();
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <NVRDisplay nvr={nvr} onEdit={() => setEditing(true)} onDeleted={onChanged} />
        )}
      </CardContent>
    </Card>
  );
}

function NVRDisplay({
  nvr,
  onEdit,
  onDeleted,
}: {
  nvr: NVRRecord;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const { t } = useT();
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;
  const [showPw, setShowPw] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [armed, setArmed] = useState(false);
  // Import-cameras (#24) — BE endpoint existed (task #40) but FE
  // never wired a button, so operator hand-inserted Camera rows via
  // Django shell on Riders Lodge. Last-import summary stays on screen
  // until the operator dismisses it or re-imports.
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<FetchCamerasResponse | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const onImport = async () => {
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const { data } = await fetchNvrCameras(nvr.id);
      setImportResult(data);
      // Refresh the parent's Cameras + Providers tables (camera-list
      // SWR keys defined in EquipmentSettings.tsx). Global mutate so
      // we don't need to plumb yet-another callback through.
      void swrMutate(`equipment:cameras:${nvr.zone_id}`);
      void swrMutate(`equipment:providers:${nvr.zone_id}`);
    } catch (e) {
      setImportError(extractApiError(e, t('error.general')));
    } finally {
      setImporting(false);
    }
  };

  // Two-click destructive — matches the Router/LocalComputer delete pattern.
  const onDelete = async () => {
    if (!armed) {
      setArmed(true);
      window.setTimeout(() => setArmed(false), 5000);
      return;
    }
    setDeleting(true);
    try {
      await deleteNVR(nvr.id);
      onDeleted();
    } finally {
      setDeleting(false);
    }
  };

  const hasIdentity =
    nvr.serial_number || nvr.firmware_version || nvr.device_id || nvr.mac_address;

  return (
    <div className="space-y-2 text-sm">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <Field
          label={t('equipment.nvr.ipAddress')}
          value={`${nvr.ip_address || '—'}:${nvr.port}`}
          mono
        />
        <Field label={t('equipment.nvr.vendor')} value={nvr.vendor || '—'} mono />
        <Field label={t('equipment.nvr.username')} value={nvr.username || '—'} mono />
        <div className="space-y-0.5">
          <div className="text-xs text-muted-foreground">{t('equipment.nvr.password')}</div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">
              {nvr.password ? (showPw ? nvr.password : '••••••••') : '—'}
            </span>
            {nvr.password && (
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={showPw ? t('equipment.nvr.hide') : t('equipment.nvr.show')}
              >
                {showPw ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {hasIdentity && (
        <div className="space-y-1 border-t pt-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('equipment.nvr.identity')}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {nvr.model && <Field label={t('equipment.nvr.model')} value={nvr.model} mono />}
            {nvr.serial_number && (
              <Field label={t('equipment.nvr.serialNumber')} value={nvr.serial_number} mono />
            )}
            {nvr.firmware_version && (
              <Field
                label={t('equipment.nvr.firmwareVersion')}
                value={nvr.firmware_version}
                mono
              />
            )}
            {nvr.mac_address && (
              <Field label={t('equipment.nvr.macAddress')} value={nvr.mac_address} mono />
            )}
            {nvr.device_id && (
              <Field label={t('equipment.nvr.deviceId')} value={nvr.device_id} mono />
            )}
          </div>
        </div>
      )}

      {nvr.notes && (
        <div className="whitespace-pre-wrap border-t pt-2 text-sm text-muted-foreground">
          {nvr.notes}
        </div>
      )}

      {(importResult || importError) && (
        <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
          {importError ? (
            <span className="text-destructive">{importError}</span>
          ) : importResult ? (
            <span className="text-muted-foreground">
              {t('equipment.nvr.importResult', {
                added: importResult.new_cameras.length,
                existing: importResult.existing_cameras.length,
                errors: importResult.errors.length,
              })}
            </span>
          ) : null}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 border-t pt-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onEdit}>
            {t('common.edit')}
          </Button>
          {/* Import cameras + admin tunnel: gated to superusers
              while the per-zone NVR flow is still rough — both
              currently surface raw BE errors (HTML / ErrorDetail
              strings) when the underlying request fails, which is
              the common case on Pi-onboarded branches. Operators
              get a quieter card; superusers can still drive the
              flow when debugging. */}
          {isSuperuser && (
            <>
              <Button variant="outline" size="sm" onClick={onImport} disabled={importing}>
                {importing ? (
                  <Loader2 className="mr-1 size-3.5 animate-spin" />
                ) : (
                  <Download className="mr-1 size-3.5" />
                )}
                {t('equipment.nvr.importCameras')}
              </Button>
              <AdminTunnelButton
                kind="nvrs"
                id={nvr.id}
                capability={nvr.admin_tunnel_capability}
              />
            </>
          )}
        </div>
        <Button variant="destructive" size="sm" onClick={onDelete} disabled={deleting}>
          {deleting ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Trash2 className="size-3.5" />
          )}
          {armed ? t('equipment.nvr.deleteConfirm') : t('equipment.nvr.delete')}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`break-all text-sm${mono ? ' font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function NVRForm({
  nvr,
  onSaved,
  onCancel,
}: {
  nvr: NVRRecord;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [ipAddress, setIpAddress] = useState(nvr.ip_address || '');
  const [port, setPort] = useState(String(nvr.port ?? 80));
  const [username, setUsername] = useState(nvr.username || '');
  const [password, setPassword] = useState(nvr.password || '');
  const [notes, setNotes] = useState(nvr.notes || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIpAddress(nvr.ip_address || '');
    setPort(String(nvr.port ?? 80));
    setUsername(nvr.username || '');
    setPassword(nvr.password || '');
    setNotes(nvr.notes || '');
  }, [nvr]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const portNumber = Number(port);
    const payload: NVRUpsert = {
      ip_address: ipAddress.trim(),
      port: Number.isFinite(portNumber) && portNumber > 0 ? portNumber : 80,
      username: username.trim(),
      password,
      notes,
    };
    try {
      await updateNVR(nvr.id, payload);
      onSaved();
    } catch (e) {
      if (isAxiosError(e)) {
        setError(extractApiError(e, t('error.general')));
      } else {
        setError(t('error.general'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5 col-span-2">
          <Label htmlFor={`nvr-ip-${nvr.id}`}>{t('equipment.nvr.ipAddress')}</Label>
          <Input
            id={`nvr-ip-${nvr.id}`}
            value={ipAddress}
            onChange={e => setIpAddress(e.target.value)}
            placeholder="192.168.1.65"
            className="font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`nvr-port-${nvr.id}`}>{t('equipment.nvr.port')}</Label>
          <Input
            id={`nvr-port-${nvr.id}`}
            type="number"
            value={port}
            onChange={e => setPort(e.target.value)}
            placeholder="80"
            className="font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`nvr-vendor-${nvr.id}`}>{t('equipment.nvr.vendor')}</Label>
          <Input
            id={`nvr-vendor-${nvr.id}`}
            value={nvr.vendor || 'hikvision'}
            readOnly
            className="bg-muted font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`nvr-user-${nvr.id}`}>{t('equipment.nvr.username')}</Label>
          <Input
            id={`nvr-user-${nvr.id}`}
            value={username}
            onChange={e => setUsername(e.target.value)}
            className="font-mono"
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`nvr-pw-${nvr.id}`}>{t('equipment.nvr.password')}</Label>
          <Input
            id={`nvr-pw-${nvr.id}`}
            name={`nvr-secret-${nvr.id}`}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="font-mono"
            autoComplete="new-password"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`nvr-notes-${nvr.id}`}>{t('equipment.nvr.notes')}</Label>
        <textarea
          id={`nvr-notes-${nvr.id}`}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          className="w-full rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        />
      </div>
      {error && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button size="sm" onClick={submit} disabled={submitting}>
          {submitting && <Loader2 className="size-3.5 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
